import { apiRequest } from './client';
import type { Capabilities } from '@kioskforge/shared';

/**
 * X1.4 — admin Screens client. Mirrors A's `/api/v1/screens` DTO (docs/API.md);
 * `url` is `/s/<public_id>` or null (off / revoked / unminted), and the PIN hash
 * is never returned. Every call is documented — no undocumented endpoint.
 */

export type ScreenProfile = 'home' | 'frame' | 'desk' | 'signage' | 'kiosk' | 'custom';
export type ScreenOrientation = 'landscape' | 'portrait';
export type SizeClass = 'esp32-800x480' | 'tablet-1280x800' | 'tv-1920x1080' | 'custom';
export type ScreenStatus = 'active' | 'paused' | 'archived';
export type UrlMode = 'off' | 'public' | 'pin';

export type ScreenDto = {
  id: string;
  name: string;
  profile: ScreenProfile;
  orientation: ScreenOrientation;
  size_class: SizeClass;
  url_mode: UrlMode;
  default_playlist: string | null;
  schedule: unknown | null;
  timezone: string;
  status: ScreenStatus;
  created: string | null;
  updated: string | null;
  url: string | null;
  url_expires_at: string | null;
  public_id: string | null;
};

export type CreateScreenInput = {
  name: string;
  profile: ScreenProfile;
  orientation: ScreenOrientation;
  size_class: SizeClass;
  timezone: string;
  url_mode: UrlMode;
  status: ScreenStatus;
  url_pin?: string;
  url_expires_at?: string;
};

/** A PATCH may also set the screen's schedule (content), incl. a pinned layout. */
export type ScreenPatch = Partial<CreateScreenInput> & { schedule?: unknown | null };

export type ScreenPresence = { screen_id: string; count: number; last_seen: string | null };

/** A device record as far as this page needs it (the gateway exposes `screen`). */
export type AttachableDevice = {
  id: string;
  name?: string;
  status?: string;
  screen?: string;
  capabilities?: Capabilities;
};

export async function listScreens(): Promise<ScreenDto[]> {
  const body = await apiRequest<{ screens: ScreenDto[] }>('/screens');
  return body.screens ?? [];
}

export async function createScreen(input: CreateScreenInput): Promise<ScreenDto> {
  const body = await apiRequest<{ screen: ScreenDto }>('/screens', { method: 'POST', body: input });
  return body.screen;
}

export async function patchScreen(id: string, input: ScreenPatch): Promise<ScreenDto> {
  const body = await apiRequest<{ screen: ScreenDto }>(`/screens/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
  return body.screen;
}

export async function deleteScreen(id: string): Promise<{ ok: boolean }> {
  return apiRequest(`/screens/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Mint a fresh public_id; the old URL 404s immediately. */
export async function rotateScreenUrl(id: string): Promise<ScreenDto> {
  const body = await apiRequest<{ screen: ScreenDto }>(`/screens/${encodeURIComponent(id)}/url/rotate`, { method: 'POST' });
  return body.screen;
}

/** url_active=false until rotated. */
export async function revokeScreenUrl(id: string): Promise<ScreenDto> {
  const body = await apiRequest<{ screen: ScreenDto }>(`/screens/${encodeURIComponent(id)}/url/revoke`, { method: 'POST' });
  return body.screen;
}

/** off / public / pin; `pin` requires a 4–32 char PIN. */
export async function setScreenUrlMode(
  id: string,
  input: { url_mode: UrlMode; url_pin?: string; url_expires_at?: string | null }
): Promise<ScreenDto> {
  const body = await apiRequest<{ screen: ScreenDto }>(`/screens/${encodeURIComponent(id)}/url/mode`, { method: 'POST', body: input });
  return body.screen;
}

export async function attachDevice(screenId: string, deviceId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/screens/${encodeURIComponent(screenId)}/devices`, { method: 'POST', body: { device: deviceId } });
}

export async function detachDevice(screenId: string, deviceId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/screens/${encodeURIComponent(screenId)}/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' });
}

/** A screen's live viewer count + last-seen (in-memory server-side, X1.3). */
export async function fetchPresence(screenId: string): Promise<ScreenPresence> {
  return apiRequest<ScreenPresence>(`/screens/${encodeURIComponent(screenId)}/presence`);
}
